import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { MarketerRow } from "@/components/MarketerRow";
import { AffiliateForm } from "@/components/platform/AffiliateForm";
import { AffiliateDialogs } from "@/components/platform/AffiliateDialogs";
import { logError } from "@/lib/log";

const emptyForm = { name: "", code: "", discount_percent: 10, commission_percent: 10 };

/** Developer-only affiliate marketers: promo codes, discounts, revenue and payouts. */
export const AffiliatePanel = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [paying, setPaying] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/affiliates");
      setRows(res.data);
    } catch (err) {
      logError("Could not load affiliates", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const call = async (fn, failure) => {
    try {
      await fn();
      await load();
    } catch (err) {
      toast.error(apiError(err, failure));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/affiliates", {
        name: form.name,
        code: form.code || null,
        discount_percent: Number(form.discount_percent),
        commission_percent: Number(form.commission_percent),
      });
      setForm(emptyForm);
      await load();
      toast.success(t("add_marketer"));
    } catch (err) {
      toast.error(apiError(err, "Could not add marketer"));
    } finally {
      setBusy(false);
    }
  };

  const edit = async ({ discount_percent, commission_percent }) => {
    const row = editing;
    setEditing(null);
    await call(() => api.put(`/affiliates/${row.affiliate_id}`, {
      name: row.name,
      discount_percent: Number(discount_percent),
      commission_percent: Number(commission_percent),
      active: row.active,
    }), "Could not update");
  };

  const toggleActive = (row) => call(() => api.put(`/affiliates/${row.affiliate_id}`, {
    name: row.name, discount_percent: row.discount_percent,
    commission_percent: row.commission_percent, active: !row.active,
  }), "Could not update");

  const remove = async (row) => {
    setToDelete(null);
    await call(() => api.delete(`/affiliates/${row.affiliate_id}`), "Could not delete");
  };

  const savePayout = async (value) => {
    const row = paying;
    setPaying(null);
    await call(async () => {
      await api.post(`/affiliates/${row.affiliate_id}/payouts`, { amount: Number(value) });
      toast.success(t("payout_recorded"));
    }, "Could not record payout");
  };

  const removePayout = (row, payout) => call(
    () => api.delete(`/affiliates/${row.affiliate_id}/payouts/${payout.payout_id}`),
    "Could not delete payout",
  );

  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-5 sm:p-7 space-y-7" data-testid="affiliates-panel">
      <div>
        <p className="eyebrow">{t("affiliates")}</p>
        <p className="mt-3 text-[11px] text-[#707070] leading-relaxed max-w-2xl">{t("affiliates_hint")}</p>
      </div>

      <AffiliateForm form={form} setForm={setForm} busy={busy} onSubmit={submit} />

      <div className="space-y-3" data-testid="affiliate-list">
        {rows.map((row) => (
          <MarketerRow key={row.affiliate_id} row={row} onPay={setPaying} onToggle={toggleActive}
            onEdit={setEditing} onDelete={setToDelete} onDeletePayout={removePayout} />
        ))}
      </div>

      <AffiliateDialogs
        editing={editing} paying={paying} toDelete={toDelete}
        onEdit={edit} onPayout={savePayout} onRemove={remove}
        onCloseEditing={() => setEditing(null)}
        onClosePaying={() => setPaying(null)}
        onCloseDelete={() => setToDelete(null)}
      />
    </div>
  );
};
